import React from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import { BRAND } from '../data/brand';
import { EDUCATION_CATEGORIES, EDUCATION_ARTICLES } from '../data/education';

const ArticlePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const articleIndex = EDUCATION_ARTICLES.findIndex((a) => a.slug === slug);
  const article = EDUCATION_ARTICLES[articleIndex];

  if (!article) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <p className="font-serif text-2xl text-zinc-50 mb-4">Article not found</p>
          <button
            onClick={() => navigate('/learn')}
            className="font-sans text-xs tracking-[0.2em] uppercase text-zinc-400 hover:text-white transition-colors"
          >
            Back to Library
          </button>
        </div>
      </div>
    );
  }

  const category = EDUCATION_CATEGORIES.find((c) => c.id === article.category);
  const prevArticle = articleIndex > 0 ? EDUCATION_ARTICLES[articleIndex - 1] : null;
  const nextArticle = articleIndex < EDUCATION_ARTICLES.length - 1 ? EDUCATION_ARTICLES[articleIndex + 1] : null;

  const relatedArticles = EDUCATION_ARTICLES
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />

      {/* Back Link */}
      <section className="pt-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-3xl mx-auto">
          <button
            onClick={() => navigate('/learn')}
            data-testid="back-to-learn"
            className="inline-flex items-center gap-2 font-sans text-xs tracking-[0.15em] uppercase text-zinc-500 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-3 h-3" />
            Library
          </button>
        </div>
      </section>

      {/* Article Header */}
      <section className="pt-8 pb-12 px-6 md:px-12 lg:px-24">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-zinc-500">
                {category?.label}
              </p>
              <span className="w-1 h-1 bg-zinc-700 rounded-full" />
              <p className="font-sans text-[10px] text-zinc-600">{article.readTime}</p>
            </div>
            <h1
              className="font-serif text-4xl sm:text-5xl font-light text-zinc-50 mb-4"
              data-testid="article-title"
            >
              {article.title}
            </h1>
            <p className="font-serif text-lg text-zinc-400 italic">
              {article.subtitle}
            </p>
          </motion.div>
          <div className="w-full h-px bg-white/5 mt-10" />
        </div>
      </section>

      {/* Article Body */}
      <section className="pb-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-3xl mx-auto space-y-12">
          {article.sections.map((section, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <h2 className="font-serif text-2xl text-zinc-100 mb-4">{section.heading}</h2>
              <p className="font-sans text-sm text-zinc-400 leading-[1.8]">{section.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Prev/Next Navigation */}
      <section className="px-6 md:px-12 lg:px-24 border-t border-white/5">
        <div className="max-w-3xl mx-auto grid grid-cols-2 gap-8 py-10">
          {prevArticle ? (
            <button
              onClick={() => navigate(`/learn/${prevArticle.slug}`)}
              data-testid="prev-article"
              className="text-left group"
            >
              <p className="font-sans text-[10px] tracking-[0.15em] uppercase text-zinc-600 mb-1 flex items-center gap-1">
                <ArrowLeft className="w-3 h-3" /> Previous
              </p>
              <p className="font-serif text-lg text-zinc-300 group-hover:text-white transition-colors">
                {prevArticle.title}
              </p>
            </button>
          ) : <div />}
          {nextArticle ? (
            <button
              onClick={() => navigate(`/learn/${nextArticle.slug}`)}
              data-testid="next-article"
              className="text-right group"
            >
              <p className="font-sans text-[10px] tracking-[0.15em] uppercase text-zinc-600 mb-1 flex items-center justify-end gap-1">
                Next <ArrowRight className="w-3 h-3" />
              </p>
              <p className="font-serif text-lg text-zinc-300 group-hover:text-white transition-colors">
                {nextArticle.title}
              </p>
            </button>
          ) : <div />}
        </div>
      </section>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="py-16 px-6 md:px-12 lg:px-24 bg-[#0A0A0A]">
          <div className="max-w-5xl mx-auto">
            <p className="font-sans text-xs tracking-[0.3em] uppercase text-zinc-500 mb-8 text-center">
              More in {category?.label}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {relatedArticles.map((related) => (
                <div
                  key={related.slug}
                  onClick={() => navigate(`/learn/${related.slug}`)}
                  className="group cursor-pointer border border-white/5 p-5 hover:border-white/15 transition-all duration-300"
                  data-testid={`related-${related.slug}`}
                >
                  <p className="font-sans text-[10px] text-zinc-600 mb-2">{related.readTime}</p>
                  <h3 className="font-serif text-lg text-zinc-200 group-hover:text-white transition-colors mb-1">
                    {related.title}
                  </h3>
                  <p className="font-sans text-xs text-zinc-500">{related.subtitle}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <p className="font-serif text-xl text-zinc-50">Secret 13</p>
          <p className="font-sans text-xs text-zinc-600">{BRAND.legal.copyright} {BRAND.legal.membersOnly}</p>
        </div>
      </footer>
    </div>
  );
};

export default ArticlePage;
